import { useMemo } from 'react'
import { useShallow } from 'zustand/shallow'

import type { Item } from '@/stores/CartStore'
import useCartStore from '@/stores/CartStore'
import useLimitStore from '@/stores/LimitStore'
import { DELUXE_SINGLE_PROBE_HOLDER, ITEMS_NA, PORT_EXPANDER_RNUMBER, PORTS_LIMIT, SENSOR_CATEGORY } from '@/util/Hardcoded'

const useStepperControls = (item: Item) => {
  const increaseCart = useCartStore(state => state.increase)
  const decreaseCart = useCartStore(state => state.decrease)
  const increaseLimit = useLimitStore(store => store.increase)
  const decreaseLimit = useLimitStore(store => store.decrease)
  const isDeluxeProbeHolder = item.partId === DELUXE_SINGLE_PROBE_HOLDER

  const amount = useCartStore(
    useShallow(store =>
      store.items.find(storeItem => storeItem.partId === item.partId)?.amount ?? 0,
    ),
  )

  const categoryLimit = useLimitStore(
    useShallow(state =>
      state.limits.some(limit =>
        limit.categoryId === item.categoryId
        && limit.hubId === item.hubId
        && limit.hit,
      ),
    ),
  )

  const itemLimit = useCartStore(useShallow(
    store => !!(
      item.limit
      && store.getByRNumberAndHub(item.hubId, item.rNumber)
      && store.getByRNumberAndHub(item.hubId, item.rNumber)!.amount >= item.limit
    ),
  ))

  const limitHit = categoryLimit || itemLimit

  // ? Disable decrease for Port Expander when there are more than 4 sensors
  const sensorPortsLimitHit = useLimitStore(
    useShallow(state =>
      state.limits.some(limit =>
        item.rNumber === PORT_EXPANDER_RNUMBER
        && limit.categoryId === SENSOR_CATEGORY
        && limit.hubId === item.hubId
        && limit.amount > PORTS_LIMIT,
      ),
    ),
  )

  const columnPreselect = useCartStore(
    useShallow(store =>
      store.items.some(storeItem =>
        storeItem.categoryId === item.categoryId
        && storeItem.hubId === item.hubId
        && storeItem.preselect),
    ),
  )

  // ? In ReefControl Pro kit case, avoids showing up unpicked items as NA instead of disabled
  // ? N/A should just be when every amount is preselect: eg limit 2 and two preselect items, not limit 7 and 3 preselect items
  const everyPickedItemIsPreselect = useCartStore(
    useShallow(store =>
      store.items
        .filter(storeItem =>
          storeItem.categoryId === item.categoryId
          && storeItem.hubId === item.hubId
          && storeItem.amount > 0,
        )
        .every(storeItem => storeItem.preselect && storeItem.amount === 1),
    ),
  )

  const itemPreselect = useMemo(() => item.preselect, [item.preselect])
  const isDisabled = useMemo(() => limitHit && amount === 0, [limitHit, amount])

  const isNA = useMemo(() =>
    (everyPickedItemIsPreselect && isDisabled) || ITEMS_NA.includes(item.partId), [everyPickedItemIsPreselect, isDisabled, item.partId])
  const hideButtons = useMemo(() =>
    (categoryLimit && itemPreselect && amount === 1) || isNA, [categoryLimit, itemPreselect, amount, isNA])

  const decreaseDisabled = useMemo(() =>
    isDeluxeProbeHolder ? amount === 3 : itemPreselect ? amount === 1 : sensorPortsLimitHit || amount === 0, [isDeluxeProbeHolder, amount, itemPreselect, sensorPortsLimitHit])
  const increaseDisabled = useMemo(() => limitHit, [limitHit])

  const increase = () => {
    increaseCart(item)
    increaseLimit(item.categoryId, item.hubId)
  }

  const decrease = () => {
    decreaseCart(item)
    decreaseLimit(item.categoryId, item.hubId)
  }

  return {
    increase,
    decrease,
    amount,
    preselect: columnPreselect,
    isDisabled,
    isNA,
    hideButtons,
    decreaseDisabled,
    increaseDisabled,
  }
}

export default useStepperControls
